/** @jsxImportSource @emotion/react */
import * as turf from '@turf/turf';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';
import LoadingOverlay from 'react-loading-overlay-ts';
import { Alert, AlertTitle, Button, ButtonGroup, IconButton, Paper, Snackbar, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import SignalWifi3BarIcon from '@mui/icons-material/SignalWifi3Bar';
import SignalWifi1BarIcon from '@mui/icons-material/SignalWifi1Bar';
import SettingsIcon from '@mui/icons-material/Settings';

import { AutoSaveBtn } from '@components/AutoSaveBtn';
import { ViewHeader } from '@components/ViewHeader';
import MapView, { mapStyles } from '@components/MapView/MapView';
import CharacteristicSections from '@components/FieldVisitWizard/CharacteristicSections/CharacteristicSections';
import { GpsSimulationControls } from '@components/FieldVisitWizard/GpsSimulationControls';
import { Characteristic } from '@components/FieldVisitWizard/Characteristic';
import { MAP_LAYERS } from '@components/FieldMapOptionsModal/FieldMapOptionsModal';
import { Alerts } from '@components/FieldVisitWizard/FieldVisitAlertsModal';
import { DownloadFieldDataBanner } from '@components/DownloadFieldDataBanner';
import { LocationWarningType } from '@components/LocationWarningModal/LocationWarningModal';
import { useAppTheme } from '@hooks/useAppTheme';
import { SITE_MODE, useOnSiteModal } from '@hooks/modals/useOnSiteModal';
import { useFieldMapOptionsModal } from '@hooks/modals/useFieldMapOptionsModal';
import { useGetFieldTables } from '@hooks/useGetFieldTables';
import { useGetFieldVisitsSteps } from '@hooks/useGetFieldVisitsSteps';
import { useSubmitsFieldDataAsync } from '@hooks/useSubmitsFieldDataAsync';
import { useSubmitsFieldDataSync } from '@hooks/useSubmitsFieldDataSync';
import { useLocationWarningModal } from '@hooks/modals/useLocationWarningModal';
import { useMarkerDetailsModal } from '@hooks/modals/useMarkerDetailsModal';
import { useFieldVisitAlertsModal } from '@hooks/modals/useFieldVisitAlertsModal';
import { useLostChangesVisitModal } from '@hooks/modals/useLostChangesVisitModal';
import { useUnableAccessDeviceLocationModal } from '@hooks/modals/useUnableAccessDeviceLocationModal';
import { FieldVisitProgressObjStore, LocalFieldVisit, LocalVisitProgress } from '@mytypes/local';
import { Position } from '@mytypes/map';
import { FieldVisitStep, Page, QUESTION_TYPE, STEP_STATUS } from '@mytypes/protocol';
import { alertQuestion } from '@mytypes/fieldVisitWizard';
import {
  clearFieldVisitProgressWithDexie,
  getFieldVisitProgressWithDexie,
  getFieldVisitForWizardWithDexie,
  setFieldVisitProgressWithDexie,
  createFieldVisitWithDexie,
  updateFieldVisitWithDexie,
  getFieldWithDexie,
  getAllFieldVisitsWithDexie,
  setFieldVisitProgressLastSavedWithDexie
} from '@services/local';
import {
  setPages,
  setFieldBoundary as setFieldBoundaryState,
  setSelectedMarker,
  setPreviousVisitsSelection,
  setChangedCharacteristic,
  selectFieldVisitWizardMapStyle,
  setInVisit
} from '@state-mgmt/slices/visitSlice';
import { RootState } from '@state-mgmt/store';
import { setShowAppNavigator, setWatchId, setCurrentPosition, setAccuracy, setSyncEnabled, selectSaveInProgress } from '@state-mgmt/slices/appSlice';
import { generateFakeCoords } from '@utils/generateFakeCoords';
import { getFieldCenterPoint } from '@utils/getFieldCenterPoint';
import { groupAlertsQuestionsByPage } from '@utils/groupAlertsQuestionsByPage';
import { wait } from '@utils/wait';
import config from '../../../config';
import { styles } from './styles';
import { useRemoveLocalFieldVisitAsync } from '@hooks/useRemoveFieldVisitAsync';
import usePreviousVisitMarkers from '@components/PreviousVisitMarkers/usePreviousVisitMarkers';
import useImagery from '@hooks/useImagey';
import { useGetSoilTable } from '@hooks/useGetSoilTable';
import useFetchClientFieldVisits from '@hooks/useFetchClientFieldVisits';
import { useGetFieldNotes } from '@hooks/useGetFieldNotes';
import { useFieldNotesMarkers } from '@hooks/useFieldNoteMarkers';

const FieldVisitWizard = () => {
  const { t } = useTranslation();
  // these ids can be trusted fieldId, protocolId, stepId
  const { downloadId, fieldId, protocolId, stepId } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useAppTheme({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const characteristicSectionRef = useRef<HTMLDivElement>();
  const { showBoundary } = useErrorBoundary();

  const {
    accuracy,
    clientId: appClientId,
    currentPosition,
    online,
    offSite,
    simulateGPS,
    trialId: appTrialId,
    watchId
  } = useSelector((state: RootState) => state.app);
  const { clients } = useSelector((state: RootState) => state.client);
  const { fields, trialGrowers, trialProducts, trials } = useSelector((state: RootState) => state.trial);
  const { protocols } = useSelector((state: RootState) => state.protocol);
  const { changedCharacteristic, pages, previousVisitsSelection } = useSelector((state: RootState) => state.visit);
  const mapStyle = useSelector(selectFieldVisitWizardMapStyle);
  const saveInProgress = useSelector(selectSaveInProgress);

  const { displayModal: displayFieldVisitAlertsModal, readyToSubmit } = useFieldVisitAlertsModal();
  const { displayModal: displayLocationWarningModal } = useLocationWarningModal();
  const { displayModal: displayLostChangesVisitModal } = useLostChangesVisitModal();
  const { displayModal: displayUnableAccessDeviceLocationModal } = useUnableAccessDeviceLocationModal();
  const { displayModal: displayOffSiteModal, siteMode, modalContent: onSiteModalContent } = useOnSiteModal();
  const [, removeLocalFieldVisit] = useRemoveLocalFieldVisitAsync();

  const [hasInProgressChanges, setHasInProgressChanges] = useState<boolean>(false);
  const [checked, setChecked] = useState<string[]>([]);
  const [downloadLastUpdate, setDownloadLastUpdate] = useState<any>();
  const [fakeCoords, setFakeCoords] = useState<any[] | undefined>();
  const [fieldBoundary, setFieldBoundary] = useState<any | undefined>();
  const [trialAreaGeoJson, setTrialAreaGeoJson] = useState<any | undefined>();
  const [getFieldTablesState, getFieldTables] = useGetFieldTables();
  const [, getSoilTable] = useGetSoilTable();
  const [getFieldVisitsStepsState, getFieldVisitsSteps] = useGetFieldVisitsSteps();
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [localId, setLocalId] = useState<number>();
  const [markerFocus, setMarkerFocus] = useState<string | undefined>();
  const [markers, setMarkers] = useState<any[]>([]);
  const [savedValues, setSavedValues] = useState<any[]>([]);
  const [showEditMarkerLocationMessage, setShowEditMarkerLocationMessage] = useState(false);
  const [showError, setShowError] = useState(false);
  const [simulationIndex, setSimulationIndex] = useState<number>(0);
  const [soilGeojson, setSoilGeojson] = useState<any | undefined>();
  const [step, setStep] = useState<number>(0);
  const submitsFieldDataSync = useSubmitsFieldDataSync();
  const [submitsFieldDataState, submitsFieldData] = useSubmitsFieldDataAsync();
  const [treatmentZoneLegends, setTreatmentZoneLegends] = useState<{ label: string; color: string }[]>([]);
  const [zoneGeojson, setZoneGeojson] = useState<any | undefined>();
  const [previousVisits, setPreviousVisits] = useState<FieldVisitStep[]>([]);
  const [stepName, setStepName] = useState<string>();
  const [isOffSiteByPrompt, setIsOffSiteByPrompt] = useState<boolean>(false);
  const [isSavingToProgress, setIsSavingToProgress] = useState<boolean>(false);
  const [isSavingToApi, setIsSavingToApi] = useState<boolean>(false);
  const [progress, setProgress] = useState<FieldVisitProgressObjStore>();

  const [fieldVisitClientId, setFieldVisitClientId] = useState<string | null>(null);
  const [fieldVisitTrialId, setFieldVisitTrialId] = useState<string | null>(null);

  const [fieldVisitClientName, setFieldVisitClientName] = useState<string>('');
  const [fieldVisitProtocolName, setFieldVisitProtocolName] = useState<string>('');
  const [fieldVisitTrialName, setFieldVisitTrialName] = useState<string>('');
  const [fieldVisitFieldName, setFieldVisitFieldName] = useState<string>('');
  const [fieldVisitGrowerName, setFieldVisitGrowerName] = useState<string>('');
  const [fieldVisitTrialProducts, setFieldVisitTrialProducts] = useState<
    {
      color: string;
      id: string;
      name: string;
    }[]
  >([]);

  const [isSavingDexieRecord, setIsSavingDexieRecord] = useState<boolean>(false);

  const [loadedFromOfflineData, setLoadedFromOfflineData] = useState<boolean>(false);

  const allTrialProducts = useMemo(() => {
    let all: {
      color: string;
      id: string;
      name: string;
    }[] = [...trialProducts, ...fieldVisitTrialProducts];

    return all;
  }, [trialProducts, fieldVisitTrialProducts]);

  // const [intervalGPS, setIntervalGPS] = useState<any>();

  useEffect(() => {
    const client = clients?.find(c => c.id === fieldVisitClientId);
    if (client?.name) {
      setFieldVisitClientName(client.name);
    }
  }, [clients, fieldVisitClientId]);

  useEffect(() => {
    const protocol = protocols?.find(p => p.id === protocolId);
    if (protocol?.name) {
      setFieldVisitProtocolName(protocol.name);
    }
  }, [protocols, protocolId]);

  useEffect(() => {
    const trial = trials?.find(t => t.id === fieldVisitTrialId);
    if (trial?.name) {
      setFieldVisitTrialName(trial.name);
    }
  }, [fieldVisitTrialId, trials]);

  useEffect(() => {
    const field = fields?.find(f => f.field_id === fieldId && f.protocol_id === protocolId);
    if (field) {
      setFieldVisitFieldName(field.name);

      const grower = trialGrowers.find(tg => tg.user_id === field?.owner_id);

      if (grower) {
        setFieldVisitGrowerName(`${grower.first_name} ${grower.last_name}`);
      }
    }
  }, [fieldId, protocolId, fields, trialGrowers]);

  useFetchClientFieldVisits({
    clientId: fieldVisitClientId || '',
    fieldId: fieldId || '',
    trialId: fieldVisitTrialId || '',
    protocolId: protocolId || ''
  });

  useEffect(() => {
    dispatch(setInVisit(true));
    return () => {
      dispatch(setInVisit(false));
    };
  }, [dispatch]);

  const accuracyIcon = useMemo(() => {
    if (accuracy) {
      if (accuracy <= config.REACT_APP_ACCURACY_GOOD) return <SignalWifi4BarIcon color="success" sx={{ fontSize: 18 }} />;
      if (accuracy <= config.REACT_APP_ACCURACY_FAIR) return <SignalWifi3BarIcon color="warning" sx={{ fontSize: 18 }} />;
      return <SignalWifi1BarIcon color="error" sx={{ fontSize: 18 }} />;
    }
  }, [accuracy]);

  const skipLocationChecks = useMemo(() => isOffSiteByPrompt || offSite, [isOffSiteByPrompt, offSite]);

  useEffect(() => {
    dispatch(setPages([]));
  }, []);

  useEffect(() => {
    displayOffSiteModal();
  }, []);

  useEffect(() => {
    if (siteMode === SITE_MODE.OFF) {
      setIsOffSiteByPrompt(true);
    } else {
      setIsOffSiteByPrompt(false);
    }
  }, [siteMode]);

  const { selectedImage } = useImagery({ fieldId, clientId: fieldVisitClientId || '', trialId: fieldVisitTrialId, protocolId });

  useEffect(() => {
    const run = async () => {
      dispatch(setShowAppNavigator(false));
      if (!fieldId || !protocolId || !stepId) return;

      const progress = await getFieldVisitProgressWithDexie();

      if (progress) {
        // load from progress

        setFieldVisitProtocolName(progress.protocolName);
        setFieldVisitClientName(progress.clientName);
        setFieldVisitTrialName(progress.trialName);
        setFieldVisitFieldName(progress.fieldName);
        setFieldVisitGrowerName(progress.growerName);

        setFieldVisitTrialId(progress.trialId);
        setFieldVisitClientId(progress.clientId || appClientId || ''); // this is a fallback that is hopefully never needed.   progress wasn't tracking clientId so its possible during updates a user could have old data missing clientId O,o
        initSections(null);
        setProgress(progress);
        handleOnMarkerLocationClicked(progress?.lastChangedCharacteristic);
        if (progress.currentStepIndex) {
          setStep(progress.currentStepIndex);
        }

        if (progress.boundaries) {
          if (progress.boundaries.boundary) {
            setFieldBoundary(progress.boundaries.boundary);
          }
          if (progress.boundaries.trialArea) {
            setTrialAreaGeoJson(progress.boundaries.trialArea);
          }
          if (progress.boundaries.zone) {
            setZoneGeojson(progress.boundaries.zone);
          }
          if (progress.boundaries.soilGeojson) {
            setSoilGeojson(progress.boundaries.soilGeojson);
          }
        }
        if (progress.lastUpdated) {
          if (!progress.lastSaved) {
            setHasInProgressChanges(true);
          } else {
            setHasInProgressChanges(progress.lastUpdated > progress.lastSaved);
          }
        }
        if (progress.trialProducts?.length) {
          setFieldVisitTrialProducts(progress.trialProducts);
        }
        setLoadedFromOfflineData(true);
      } else if (downloadId) {
        // load from planner
        // fetch from planner
        getFieldWithDexie(Number(downloadId)).then(item => {
          if (item) {
            const localvisits = item.visits;
            if (simulateGPS) {
              setFakeCoords(generateFakeCoords(item.boundaries.field.features.at(0).geometry, 50));
            }

            setFieldVisitProtocolName(item.protocolName);
            setFieldVisitClientName(item.clientName);
            setFieldVisitTrialName(item.trialName);
            setFieldVisitFieldName(item.fieldName);
            setFieldVisitGrowerName(item.growerName || '');
            setFieldVisitTrialProducts(item.trialProducts || []);

            setFieldVisitClientId(item.clientId);
            setFieldVisitTrialId(item.trialId);
            setDownloadLastUpdate(item.createdAt);

            // hack to wait to the map load
            wait(0).then(() => {
              setFieldBoundary(item.boundaries.field);
              setTrialAreaGeoJson(item.boundaries.area);
              setZoneGeojson(item.boundaries.treatmentsZones);
              setSoilGeojson(item.boundaries.soil);
            });
            getFieldVisitForWizardWithDexie(fieldId, protocolId, stepId).then(visit => {
              if (visit) {
                // use from local
                //   setIsEditMode(true);
                setLocalId(visit.id);
                initSections(visit);

                if (visit.lastUpdated) {
                  if (!visit.lastSaved) {
                    setHasInProgressChanges(true);
                  } else {
                    setHasInProgressChanges(visit.lastUpdated > visit.lastSaved);
                  }
                }
              } else {
                initSections(step);
              }
            });

            const step = localvisits.filter((localvisit: any) => localvisit.stepId === stepId).at(0);
            if (step) {
              initSections(step);
            }
            setLoadedFromOfflineData(true);
          }
        });
      } else {
        // Load from live data
        if (appTrialId && appClientId) {
          setFieldVisitTrialId(appTrialId);
          setFieldVisitClientId(appClientId);
          getFieldTables(appTrialId, protocolId, fieldId, true, false);
          getFieldVisitForWizardWithDexie(fieldId, protocolId, stepId).then(visit => {
            if (visit) {
              // use from local
              // setIsEditMode(true);
              setLocalId(visit.id);
              initSections(visit);

              if (visit.lastUpdated) {
                if (!visit.lastSaved) {
                  setHasInProgressChanges(true);
                } else {
                  setHasInProgressChanges(visit.lastUpdated > visit.lastSaved);
                }
              }
              setLoadedFromOfflineData(true);
            } else {
              // fetch from server
              getFieldVisitsSteps(appTrialId, protocolId, fieldId, [stepId], true);
              setLoadedFromOfflineData(false);
            }
          });
        }
      }
    };

    run();
  }, [fieldId]);

  useEffect(() => {
    if (getFieldVisitsStepsState.data) {
      const step = getFieldVisitsStepsState.data?.find((step: any) => step.stepId === stepId);
      if (step) {
        setIsEditMode(step.mainActionCompleted === STEP_STATUS.COMPLETE);
        initSections(step);
      }
    }
  }, [getFieldVisitsStepsState.data]);

  useEffect(() => {
    if (fakeCoords) {
      dispatch(
        setCurrentPosition({
          latitude: fakeCoords[simulationIndex].latitude,
          longitude: fakeCoords[simulationIndex].longitude
        })
      );
    }
  }, [simulationIndex]);

  // useEffect(() => {
  //   if (fakeCoords && !isDragging) {
  //     setIntervalGPS(
  //       setInterval(() => {
  //         setSimulationIndex(prevstate => (prevstate < fakeCoords.length - 1 ? prevstate + 1 : 0));
  //       }, 5000)
  //     );
  //   }
  // }, [fakeCoords, isDragging]);

  useEffect(() => {
    if (isDragging) {
      stopGettingPosition();
      // clearInterval(intervalGPS);
    } else {
      startGettingPosition();
    }
  }, [isDragging]);

  useEffect(() => {
    if (zoneGeojson && Array.isArray(zoneGeojson.features)) {
      let treatmentZones: { label: string; color: string; productId: string }[] = [];
      zoneGeojson.features.forEach((treatmentZone: any) => {
        const zoneProductId = treatmentZone.properties.product_id;
        if (zoneProductId && !treatmentZones.some(tz => tz.productId === zoneProductId)) {
          const product = allTrialProducts.find(trialProduct => trialProduct.id === zoneProductId);
          if (product) {
            treatmentZones = [...treatmentZones, { label: product.name, color: product.color, productId: zoneProductId }];
          }
        }
      });
      setTreatmentZoneLegends(treatmentZones);
    }
  }, [zoneGeojson, allTrialProducts]);

  useEffect(() => {
    if (getFieldTablesState.data) {
      if (getFieldTablesState.data.field) {
        const data = getFieldTablesState.data.field;
        setFieldBoundary(data);

        if (!currentPosition && !simulateGPS) {
          displayUnableAccessDeviceLocationModal('init');
        }
        if (simulateGPS) {
          setFakeCoords(generateFakeCoords(data.features.at(0).geometry, 50));
        }
      }

      if (getFieldTablesState.data.area) {
        setTrialAreaGeoJson(getFieldTablesState.data.area);
      }

      if (getFieldTablesState.data.treatmentsZones) {
        setZoneGeojson(getFieldTablesState.data.treatmentsZones);
      }
      if (getFieldTablesState.data.soil) {
        const data = getFieldTablesState.data.soil;
        setSoilGeojson(data);
      } else if (getFieldTablesState.data.boundaryTableGuid) {
        getSoilTable(getFieldTablesState.data.boundaryTableGuid).then(response => {
          if (response.soil) {
            setSoilGeojson(response.soil);
          }
        });
      }
    }
  }, [getFieldTablesState.data, allTrialProducts]);

  useEffect(() => {
    if (submitsFieldDataState.isSuccess) {
      clearFieldVisitProgressWithDexie().then(() => closeWizard());
    }
  }, [submitsFieldDataState.isSuccess]);

  useEffect(() => setShowEditMarkerLocationMessage(markers.some(marker => marker.draggable)), [markers]);

  useEffect(() => {
    setMarkers(markers.map(marker => (marker.id === markerFocus ? { ...marker, focus: true, draggable: true } : { ...marker, focus: false })));
  }, [markerFocus]);

  useEffect(() => {
    readyToSubmit && saveVisit();
  }, [readyToSubmit]);

  useEffect(() => {
    if (fieldBoundary) dispatch(setFieldBoundaryState(fieldBoundary));
  }, [fieldBoundary]);

  useEffect(() => {
    updateMarkers(pages);
  }, [checked, pages]);

  const { previousVisitsMarkers, handleOnPreviousMarkerLocationClicked } = usePreviousVisitMarkers({
    visits: getFieldVisitsStepsState.data,
    stepId,
    fieldId: fieldId || '',
    trialId: fieldVisitTrialId || '',
    protocolId: protocolId || '',
    loadedFromOfflineData
  });

  useEffect(() => {
    if (changedCharacteristic) {
      setIsSavingToProgress(true);
      wait(1000).then(() => setIsSavingToProgress(false));
      if (changedCharacteristic && fieldVisitTrialId && protocolId && stepId && stepName && fieldVisitClientId && fieldId) {
        const visit: LocalVisitProgress = {
          lastChangedCharacteristic: changedCharacteristic,
          fieldId,
          fieldName: fieldVisitFieldName,
          growerName: fieldVisitGrowerName,
          pages,
          protocolId: protocolId,
          protocolName: fieldVisitProtocolName,
          savedValues,
          stepId,
          stepName,
          clientId: fieldVisitClientId,
          clientName: fieldVisitClientName,
          trialId: fieldVisitTrialId,
          trialName: fieldVisitTrialName,
          url: location.pathname + location.search,
          currentStepIndex: step,
          boundaries: {
            boundary: fieldBoundary,
            trialArea: trialAreaGeoJson,
            zone: zoneGeojson,
            soilGeojson: soilGeojson
          },
          trialProducts: fieldVisitTrialProducts?.length ? fieldVisitTrialProducts : trialProducts
        };

        setFieldVisitProgressWithDexie(visit);
        dispatch(setChangedCharacteristic(undefined));
      }
    }
  }, [changedCharacteristic]);

  const initSections = async (step: FieldVisitStep | LocalFieldVisit | null) => {
    try {
      setStepName(step?.label || '');
      const visitProgress = await getFieldVisitProgressWithDexie();
      if (visitProgress) {
        setChecked(visitProgress.pages.map(page => page.pageId));
        dispatch(setPages(visitProgress.pages));
        setStepName(visitProgress.stepName);
        return;
      }

      if (!step) throw new Error('no step or visit progress to init sections');

      const pagesOrdered = [...step.pages];
      pagesOrdered.sort((a: Page, b: Page) => a.order.toString().localeCompare(b.order.toString(), undefined, { numeric: true }));
      dispatch(setPages(pagesOrdered));
      if (step.savedValues) setSavedValues(step.savedValues);
    } catch (error) {
      showBoundary(error);
    }
  };

  const checkLocations = (question: any) => {
    try {
      if (question.valueType === QUESTION_TYPE.UPLOAD) {
        question.value = question.value.map((value: any) => (value.remove ? { ...value, latitude: undefined, longitude: undefined } : value));
        return question;
      } else {
        return question.value ? question : { ...question, latitude: undefined, longitude: undefined };
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const hasLocation = (characterizationId: string) => {
    try {
      const value = savedValues.find(savedValue => savedValue.characterization_id === characterizationId);
      return value?.location;
    } catch (error) {
      showBoundary(error);
    }
  };

  const assignValueToQuestion = (pageId: string, characterizationId: string, value: any, skipLocation?: boolean | null | undefined) => {
    setHasInProgressChanges(true);
    try {
      if (!fieldBoundary) return;
      dispatch(setChangedCharacteristic(characterizationId));
      const position = currentPosition;

      const newPages = pages.map(page => {
        let questions = page.questions;
        if (page.pageId === pageId) {
          questions = questions.map(question => {
            if (question.characterization_id === characterizationId) {
              const newQuestionObj = {
                ...question,
                value,
                ...(value && !hasLocation(characterizationId) && !skipLocationChecks && position)
              };
              return checkLocations(newQuestionObj);
            }
            return question;
          });
        }
        return { ...page, questions };
      });
      dispatch(setPages(newPages));

      // update markers
      let newMarkers: any = [];
      newPages.forEach(({ questions }) => {
        questions.forEach(answeredQuestion => {
          if (answeredQuestion.value && answeredQuestion.valueType === QUESTION_TYPE.UPLOAD) {
            answeredQuestion.value.forEach((value: any) => {
              if (value.latitude && value.longitude) {
                newMarkers.push({
                  id: answeredQuestion.characterization_id,
                  draggable: false,
                  focus: answeredQuestion.characterization_id === markerFocus ? true : false,
                  thumb: value.image || value.url,
                  latitude: value.latitude,
                  longitude: value.longitude
                });
              }
            });
          } else {
            if (answeredQuestion.latitude && answeredQuestion.longitude) {
              newMarkers.push({
                id: answeredQuestion.characterization_id,
                draggable: false,
                focus: answeredQuestion.characterization_id === markerFocus ? true : false,
                latitude: answeredQuestion.latitude,
                longitude: answeredQuestion.longitude
              });
            }
          }
        });
      });
      setMarkers(newMarkers);

      if (!skipLocationChecks && value && value.length && position && !skipLocation) {
        // @todo convert this to new util function
        const isInside = turf.booleanPointInPolygon(
          turf.point([position.longitude, position.latitude]),
          turf.polygon(fieldBoundary.features[0].geometry.coordinates)
        );
        if (!isInside) {
          displayLocationWarningModal(characterizationId, position, LocationWarningType.OUT_OF_BOUNDARIES);
          return;
        }
      }

      if (!skipLocationChecks && value && value.length && position && accuracy && accuracy > config.REACT_APP_ACCURACY_GOOD && !skipLocation) {
        displayLocationWarningModal(characterizationId, position, LocationWarningType.ACCURACY);
        return;
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const preComplete = () => {
    try {
      if (!currentPosition && !simulateGPS) {
        displayUnableAccessDeviceLocationModal('submit', () => onCompleteBtnPress());
      } else {
        onCompleteBtnPress();
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const onCompleteBtnPress = () => {
    try {
      const alerts: Alerts = {
        optionals: 0,
        optionalsList: [],
        required: 0,
        requiredList: []
      };
      const requiredQuestions: alertQuestion[] = [];
      const optionalQuestions: alertQuestion[] = [];

      pages.forEach(page => {
        if (checked.includes(page.pageId)) {
          page.questions.forEach(question => {
            if (typeof question.value === 'undefined') {
              if (question.required) {
                requiredQuestions.push({ sectionId: page.pageId, sectionLabel: page.label, question: question.label });
                alerts.required = alerts.required + 1;
              } else {
                optionalQuestions.push({ sectionId: page.pageId, sectionLabel: page.label, question: question.label });
                alerts.optionals = alerts.optionals + 1;
              }
            }
          });
        }
      });

      alerts.requiredList = groupAlertsQuestionsByPage(requiredQuestions);
      alerts.optionalsList = groupAlertsQuestionsByPage(optionalQuestions);

      if (alerts.required > 0 || alerts.optionals > 0) {
        displayFieldVisitAlertsModal(alerts);
        return;
      }
      saveVisit();
    } catch (error) {
      showBoundary(error);
    }
  };

  const hasAnsweredAtleastOneQuestion = useMemo(
    () =>
      pages.some(page => {
        return page.questions.some(question => {
          return typeof question.value !== 'undefined' && (!Array.isArray(question.value) || question.value.length);
        });
      }),
    [pages]
  );

  const closeWizard = async () => {
    try {
      stopGettingPosition();
      setShowError(false);
      setIsSavingToProgress(false);
      setIsSavingToApi(false);
      dispatch(setPages([]));
      dispatch(setFieldBoundaryState(undefined));
      dispatch(setSelectedMarker(undefined));
      dispatch(setShowAppNavigator(true));
      dispatch(setChangedCharacteristic(undefined));

      const localVisits = await getAllFieldVisitsWithDexie();
      dispatch(setSyncEnabled(localVisits.length ? true : false));

      if (searchParams.has('from') && searchParams.get('from') === 'my-visits') {
        let url = '/my-visits';
        navigate(url);
      } else {
        let url = `/field-visits/${fieldId}/${protocolId}`;
        if (downloadId) {
          url += `/${downloadId}`;
        }

        url += `?grower_id=${searchParams.get('grower_id')}`;

        if (searchParams.get('plan_id')) {
          url += `&plan_id=${searchParams.get('plan_id')}`;
        }
        if (searchParams.get('operative')) {
          url += `&operative=${searchParams.get('operative')}`;
        }

        navigate(url);
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const switchFromDownloadToOnline = () => {
    try {
      stopGettingPosition();
      setShowError(false);
      dispatch(setPages([]));
      dispatch(setShowAppNavigator(true));
      const redirectTo = encodeURIComponent(`/field-visits/${fieldId}/${protocolId}/start/${stepId}?grower_id=${searchParams.get('grower_id')}`);
      navigate(`/splash?redirectTo=${redirectTo}`);
    } catch (error) {
      showBoundary(error);
    }
  };

  /**
   * When an image is successfully uploaded to the api, update state to reflect this so it can be properly delete
   *
   * @param localId local "id" of the value
   * @param valueId db "id" for field characteristic value
   * @param url url of the saved image
   */
  const onUploadImage = (localId: string, valueId: string, url: string) => {
    const newPages = pages.map(page => {
      const questions = page.questions.map(question => {
        let value = Array.isArray(question.value)
          ? question.value.map(v => {
              if (v.id && v.id === localId) {
                return {
                  ...v,
                  valueId: valueId,
                  url: url,
                  id: undefined,
                  image: undefined
                };
              } else {
                return v;
              }
            })
          : question.value;

        return { ...question, value };
      });

      return { ...page, questions };
    });
    dispatch(setPages(newPages));
  };

  const saveVisit = async (complete: boolean = true) => {
    try {
      let fieldVisitLocalId = localId;
      if (!localId && fieldVisitTrialId && fieldVisitClientId && protocolId && stepId && fieldId) {
        setIsSavingDexieRecord(true);
        // Bug fix: use fieldClientId, fieldTrialId, and fieldProtocolId as source of truth (for offline usage)
        try {
          const id = await createFieldVisitWithDexie({
            clientId: fieldVisitClientId || '',
            clientName: fieldVisitClientName,
            trialId: fieldVisitTrialId,
            trialName: fieldVisitTrialName,
            protocolId: protocolId || '',
            protocolName: fieldVisitProtocolName,
            fieldId,
            fieldName: fieldVisitFieldName,
            stepId,
            pages,
            savedValues,
            complete
          });
          fieldVisitLocalId = id;
          setLocalId(id);
        } catch (error) {
          setIsSavingDexieRecord(false);
          console.error(error);
          throw new Error('An error occurred creating field visit dexie record');
        }
      }

      const timestamp = new Date().toISOString();

      // update the local field visit on editing mode
      if (localId) {
        setIsSavingDexieRecord(true);
        try {
          await updateFieldVisitWithDexie(localId, {
            pages,
            savedValues,
            stepId,
            complete,
            lastUpdated: timestamp
          });
        } catch (error) {
          setIsSavingDexieRecord(false);
          console.error(error);
          throw new Error('An error occurred updating field visit dexie record');
        }
      }

      setIsSavingDexieRecord(false);

      if (online && protocolId && fieldVisitLocalId) {
        if (complete) {
          setIsSavingToApi(true);
          submitsFieldData(fieldVisitLocalId, true, onUploadImage)
            .then(async () => {
              setHasInProgressChanges(false);
            })
            .finally(() => {
              setIsSavingToApi(false);
            });
        } else {
          // used to "save as we go". This is to save progress in between sections. Not as strict as the one above.
          setIsSavingToApi(true);
          submitsFieldDataSync(fieldVisitLocalId, onUploadImage)
            .then(async () => {
              setHasInProgressChanges(false);
              if (localId) {
                await updateFieldVisitWithDexie(localId, {
                  lastSaved: timestamp
                });
              }
              await setFieldVisitProgressLastSavedWithDexie(timestamp);

              const progress = await getFieldVisitProgressWithDexie();
              if (progress?.lastSaved && progress?.lastUpdated) {
                setHasInProgressChanges(progress.lastUpdated > progress.lastSaved);
              }
            })
            .finally(() => {
              setIsSavingToApi(false);
            });
        }
        return;
      } else if (complete) {
        await clearFieldVisitProgressWithDexie();
        closeWizard();
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const updateMarkers = (pages: Page[]) => {
    try {
      let newMarkers: any = [];
      pages.forEach(({ pageId, questions }) => {
        if (!checked.includes(pageId)) {
          return;
        }

        questions.forEach(question => {
          if (question.value && Array.isArray(question.value) && question.valueType === QUESTION_TYPE.UPLOAD) {
            question.value.forEach((value: any) => {
              if (value.latitude && value.longitude) {
                newMarkers.push({
                  id: question.characterization_id,
                  draggable: false,
                  focus: question.characterization_id === markerFocus ? true : false,
                  thumb: value.image || value.url,
                  latitude: value.latitude,
                  longitude: value.longitude
                });
              }
            });
          } else {
            if (question.latitude && question.longitude) {
              newMarkers.push({
                id: question.characterization_id,
                draggable: question.characterization_id === markerFocus ? true : false,
                focus: question.characterization_id === markerFocus ? true : false,
                latitude: question.latitude,
                longitude: question.longitude
              });
            }
          }
        });
      });
      setMarkers(newMarkers);
    } catch (error) {
      showBoundary(error);
    }
  };

  const handleOnFocusMarkerLocationChange = (characterizationId: string, newPosition: Position) => {
    try {
      dispatch(setChangedCharacteristic(characterizationId));

      const newPages = pages.map(page => {
        let questions = page.questions;
        questions = questions.map(question =>
          question.characterization_id === characterizationId ? { ...question, longitude: newPosition.longitude, latitude: newPosition.latitude } : question
        );

        return { ...page, questions };
      });

      dispatch(setPages(newPages));
      updateMarkers(newPages);
      setMarkerFocus(characterizationId);
    } catch (error) {
      showBoundary(error);
    }
  };

  const handleOnMarkerLocationClicked = (id: string) => {
    try {
      let currentIndex = 0;
      let targetIndexPage = 0;
      for (const page of pages) {
        if (checked.includes(page.pageId)) {
          currentIndex++;
        }
        const characteristic = page.questions.find(({ characterization_id }) => characterization_id === id);
        if (characteristic) {
          targetIndexPage = currentIndex;
        }
      }
      if (currentIndex) {
        setStep(targetIndexPage);
        dispatch(setSelectedMarker(id));
        setMarkerFocus(id);
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const handleOnQuestionExpanded = (characterizationId: string | undefined) => {
    try {
      setMarkerFocus(characterizationId);
      setMarkers(markers.map(marker => ({ ...marker, draggable: false })));
    } catch (error) {
      showBoundary(error);
    }
  };

  const handleOnReCenterLocationEnabled = (characterizationId: string) => {
    try {
      dispatch(setChangedCharacteristic(characterizationId));
      const centerPoint = getFieldCenterPoint(fieldBoundary);

      const newPages = pages.map(page => {
        let questions = page.questions;
        questions = questions.map(question => (question.characterization_id === characterizationId ? { ...question, ...centerPoint } : question));

        return { ...page, questions };
      });
      dispatch(setPages(newPages));
      updateMarkers(newPages);
    } catch (error) {
      showBoundary(error);
    }
  };

  const handleOnAddLocation = (characterizationId: string) => {
    try {
      dispatch(setChangedCharacteristic(characterizationId));
      const centerPoint = getFieldCenterPoint(fieldBoundary);
      const newPages = pages.map(page => {
        let questions = page.questions;
        questions = questions.map(question => (question.characterization_id === characterizationId ? { ...question, ...centerPoint } : question));

        return { ...page, questions };
      });
      dispatch(setPages(newPages));
      updateMarkers(newPages);
    } catch (error) {
      showBoundary(error);
    }
  };

  const startGettingPosition = () => {
    try {
      const newWatchId = navigator.geolocation.watchPosition(
        ({ coords }) => {
          dispatch(setAccuracy(coords.accuracy));
          dispatch(
            setCurrentPosition({
              latitude: coords.latitude,
              longitude: coords.longitude
            })
          );
        },
        err => console.error(`Error watching the position: ${err.message}`),
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
      dispatch(setWatchId(newWatchId));
    } catch (error) {
      showBoundary(error);
    }
  };

  const stopGettingPosition = () => {
    try {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
        dispatch(setWatchId(undefined));
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const onCancelWizard = async () => {
    try {
      const visitProgress = await getFieldVisitProgressWithDexie();
      if (visitProgress) {
        displayLostChangesVisitModal(async () => {
          if (localId) {
            await removeLocalFieldVisit(localId);
          }

          setTimeout(() => {
            closeWizard();
          }, 200);
        });
      } else {
        if (localId) {
          await removeLocalFieldVisit(localId);
        }

        setTimeout(() => {
          closeWizard();
        }, 200);
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const [getFieldNotesState] = useGetFieldNotes(fieldId || null, protocolId || null, fieldVisitTrialId || null);
  const fieldNotesMarkers = useFieldNotesMarkers(getFieldNotesState.data, 'fieldNotePopup');

  const handleLocationSettings = () => {
    displayOffSiteModal();
  };

  const {
    displayModal: displayFieldMapOptionsModal,
    selectedLayers,
    display: mapOptionsMenuDisplay
  } = useFieldMapOptionsModal({
    treatmentZones: treatmentZoneLegends,
    hasTreatmentZone: !!zoneGeojson,
    trialId: fieldVisitTrialId || '',
    protocolId: protocolId || ''
  });

  const accuracyText = useMemo(() => {
    if (!skipLocationChecks) {
      return `${t('general.accuracy')}: ${accuracy ? Math.round(accuracy) + ' mts' : '-'}`;
    } else {
      return `${t('general.accuracy')}: ${t('general.disabled')}`;
    }
  }, [skipLocationChecks, accuracy]);

  const handleManualSaveProgressError = () => {
    setShowError(true);
  };

  return (
    <>
      <LoadingOverlay
        spinner
        active={submitsFieldDataState.isLoading}
        text={t('field-visit-wizard.saving-message')}
        styles={{
          wrapper: {
            height: '100vh'
          }
        }}
      >
        <Paper css={styles.container(theme)}>
          <ViewHeader
            title={t('field-visit-wizard.title')}
            onGoBackBtnPress={onCancelWizard}
            rightBtn={
              <AutoSaveBtn
                isSaving={isSavingToApi}
                onClick={() =>
                  saveVisit(false).catch(e => {
                    handleManualSaveProgressError();
                    throw e;
                  })
                }
                hasChanges={hasInProgressChanges}
                online={online}
              />
            }
          />
          {downloadId && <DownloadFieldDataBanner downloadLastUpdate={downloadLastUpdate} onSwitchBtnPress={() => switchFromDownloadToOnline()} />}

          {simulateGPS && fakeCoords && (
            <GpsSimulationControls
              disablePreviusBtn={simulationIndex === 0}
              disableNextBtn={simulationIndex === fakeCoords.length - 1}
              onPreviusBtnClicked={() => setSimulationIndex(simulationIndex - 1)}
              onNextBtnClicked={() => setSimulationIndex(simulationIndex + 1)}
            />
          )}

          <div css={styles.mapContainer}>
            <LoadingOverlay
              spinner
              active={getFieldTablesState.isLoading || getFieldVisitsStepsState.isLoading}
              styles={{ wrapper: styles.mapLoadingOverlayWrapper }}
              text={t('field-visit-wizard.loading-map-message')}
            >
              <MapView
                trialProducts={allTrialProducts}
                mapStyle={mapStyle || mapStyles.satellite}
                fieldBoundary={fieldBoundary}
                markers={markers}
                onDraggStatusChange={dragging => setIsDragging(dragging)}
                onFocusMarkerLocationChange={handleOnFocusMarkerLocationChange}
                onMarkerLocationClicked={handleOnMarkerLocationClicked}
                onPreviousMarkerLocationClicked={handleOnPreviousMarkerLocationClicked}
                previousVisitsMarkers={previousVisitsMarkers}
                showAccuracyArea={!skipLocationChecks}
                showCurrentPosition={true}
                showEditMarkerLocationMessage={showEditMarkerLocationMessage}
                showSsurgo={selectedLayers.includes(MAP_LAYERS.SURGO)}
                showTreatmentZones={selectedLayers.includes(MAP_LAYERS.TREATMENT_ZONES)}
                showTrialArea={selectedLayers.includes(MAP_LAYERS.TRIAL_AREA)}
                soilGeojson={soilGeojson}
                trialAreaGeoJson={trialAreaGeoJson}
                zoneGeojson={zoneGeojson}
                selectedImage={selectedImage}
                fieldNotesMarkers={selectedLayers.includes(MAP_LAYERS.FIELD_NOTES) ? fieldNotesMarkers : []}
              />
            </LoadingOverlay>

            <div css={styles.mapFooter} style={{ justifyContent: 'space-between' }}>
              <div css={styles.accuracy}>
                <Typography fontSize={14}>{accuracyText}</Typography>
                {!skipLocationChecks ? accuracyIcon : null}

                <IconButton size="small" onClick={handleLocationSettings}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </div>

              <Button
                css={styles.mapOptionBtn}
                disabled={getFieldTablesState.isLoading}
                variant="text"
                startIcon={<TuneIcon />}
                onClick={() => displayFieldMapOptionsModal()}
              >
                {t('field-visit-wizard.map-options-button')}
              </Button>
            </div>
          </div>
          <div css={styles.menuWrapper}>
            <div
              // @ts-ignore
              ref={characteristicSectionRef}
              css={styles.scrollableArea(theme)}
            >
              <CharacteristicSections
                hidden={step > 0}
                fieldName={fieldVisitFieldName}
                data={pages}
                isLoading={getFieldTablesState.isLoading || getFieldVisitsStepsState.isLoading}
                onSelectionChange={selection => setChecked(selection)}
              />

              {/* characteristic sections */}
              {pages
                .filter(({ pageId }) => checked.includes(pageId))
                .map(({ pageId, label, products, questions }, index) =>
                  step === index + 1 ? (
                    <div key={`questions-${pageId}`} css={styles.characteristicContainer} style={{ display: step === index + 1 ? 'initial' : 'none' }}>
                      <Typography component={'span'} color="text.primary" fontSize={22}>
                        <div css={styles.sectionTitle}>
                          <span>{label}</span>
                          <span
                            css={styles.productBullet(theme)}
                            style={{ backgroundColor: products.length > 0 ? allTrialProducts.find(tp => tp.id === products.at(0))?.color : 'initial' }}
                          />
                        </div>
                      </Typography>

                      <Characteristic
                        questions={questions}
                        onAddLocation={handleOnAddLocation}
                        onAnswersChange={(characterizationId, value, _, skipLocation) => assignValueToQuestion(pageId, characterizationId, value, skipLocation)}
                        onQuestionExpanded={handleOnQuestionExpanded}
                        onReCenterLocationEnabled={handleOnReCenterLocationEnabled}
                      />
                    </div>
                  ) : null
                )}
            </div>

            {/* Actions Buttons */}
            <div css={styles.actionsContainer(theme)}>
              <Button variant="text" onClick={onCancelWizard} sx={{ textTransform: 'uppercase' }}>
                {t('general.cancel')}
              </Button>
              <ButtonGroup variant="contained">
                <Button
                  disabled={step === 0 || isSavingToApi}
                  onClick={() => {
                    characteristicSectionRef.current?.scrollTo(0, 0);
                    setStep(step - 1);
                  }}
                >
                  {t('general.previous')}
                </Button>
                {step === checked.length && step > 0 ? (
                  <Button
                    css={styles.actionsBtn}
                    onClick={preComplete}
                    disabled={!hasAnsweredAtleastOneQuestion || isSavingToApi || isSavingDexieRecord || saveInProgress}
                  >
                    {t('general.complete')}
                  </Button>
                ) : (
                  <Button
                    css={styles.actionsBtn}
                    disabled={
                      getFieldTablesState.isLoading ||
                      getFieldVisitsStepsState.isLoading ||
                      checked.length === 0 ||
                      isSavingToApi ||
                      isSavingDexieRecord ||
                      saveInProgress
                    }
                    onClick={() => {
                      saveVisit(false); // save as we go, especially if we are still online
                      characteristicSectionRef.current?.scrollTo(0, 0);
                      setStep(step + 1);
                    }}
                  >
                    {t('general.next')}
                  </Button>
                )}
              </ButtonGroup>
            </div>
          </div>

          {/* Error handeling */}
          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={showError} onClose={() => setShowError(false)}>
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={async () => {
                    setShowError(false);
                  }}
                  sx={{ textTransform: 'uppercase' }}
                >
                  {t('field-visit-wizard.error.try-latter-button')}
                </Button>
              }
            >
              <AlertTitle>{t('field-visit-wizard.error.title')}</AlertTitle>
              {t('field-visit-wizard.error.text')}
            </Alert>
          </Snackbar>
          {mapOptionsMenuDisplay}
        </Paper>
      </LoadingOverlay>
      {onSiteModalContent}
    </>
  );
};

export default FieldVisitWizard;
